import { CdkConnectedOverlay, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  DestroyRef,
  EventEmitter,
  HostListener,
  inject,
  input,
  Output,
  QueryList,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ClickedOutsideDirective, TemplateWithValueDirective } from '@coin/shared/core/util-directives';
import { DefaultFormValueControl, GlobalEventsService } from '@coin/shared/core/util-helpers';
import { TranslateModule } from '@ngx-translate/core';
import { animationFrames, filter, first, switchMap } from 'rxjs';
import { V2TextInputComponent } from '../text-input/v2-text-input.component';

// TODO: needs Result open animation
@Component({
  selector: 'coin-v2-search-box',
  templateUrl: './v2-search-box.component.html',
  styleUrls: ['./v2-search-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    FormsModule,
    TranslateModule,
    V2TextInputComponent,
    OverlayModule,
    ReactiveFormsModule
  ],
  hostDirectives: [
    {
      directive: ClickedOutsideDirective,
      outputs: ['clickedOutside']
    }
  ]
})
export class V2SearchBoxComponent<T = unknown> extends DefaultFormValueControl<T> implements AfterViewInit {
  private readonly destroy = inject(DestroyRef);
  private readonly events = inject(GlobalEventsService);
  private readonly cd = inject(ChangeDetectorRef);

  readonly placeholder = input<string>();
  readonly label = input<string>();
  readonly icon = input('search');
  readonly loading = input<boolean>();
  readonly noResultsTextKey = input('general.no-results-found');
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() search = new EventEmitter<string>();
  @ContentChildren(TemplateWithValueDirective) results: QueryList<TemplateWithValueDirective<T>>;

  @ViewChild(V2TextInputComponent) searchInputElement: V2TextInputComponent;
  @ViewChild(CdkConnectedOverlay) cdkOverlay: CdkConnectedOverlay;

  protected isSearchResultVisible = signal<boolean>(false);
  protected searchControl = new FormControl('');

  @HostListener('focusin')
  public onFocusEnter(): void {
    if (!this.isSearchResultVisible()) {
      this.isSearchResultVisible.set(true);
    }
  }

  @HostListener('clickedOutside')
  public handleClickedOutside() {
    if (this.searchControl.value) {
      this.isSearchResultVisible.set(false);
    }
  }

  public onEscape(): void {
    if (this.isSearchResultVisible()) {
      this.isSearchResultVisible.set(false);
      this.searchInputElement.blur();
    }
  }

  constructor() {
    super();
    this.closeResultsOnEscape();
    this.syncSearchControlDisabledState();
  }

  public ngAfterViewInit(): void {
    this.results.changes
      .pipe(
        switchMap(() => animationFrames().pipe(first())),
        takeUntilDestroyed(this.destroy)
      )
      .subscribe(() => {
        this.cd.markForCheck();
        this.cdkOverlay.overlayRef?.updatePosition();
      });

    this.searchControl.valueChanges
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroy))
      .subscribe(value => this.search.emit(value));
  }

  protected selectResult(value: T): void {
    this.isSearchResultVisible.set(false);

    this.updateValue(value);
    this.resetSearch();
  }

  protected clearValue(): void {
    this.updateValue(undefined);
    this.resetSearch();
  }

  private resetSearch(): void {
    this.searchControl.reset('', { emitEvent: false });
  }

  protected handleDetach(): void {
    if (this.viewValue()) {
      this.resetSearch();
    }
  }

  private closeResultsOnEscape(): void {
    this.events
      .listen('keydown')
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        if (event.key === 'Escape') {
          this.onEscape();
        }
      });
  }

  private syncSearchControlDisabledState(): void {
    toObservable(this.disabled)
      .pipe(takeUntilDestroyed())
      .subscribe(isDisabled =>
        isDisabled ? this.searchControl.disable({ emitEvent: false }) : this.searchControl.enable({ emitEvent: false })
      );
  }
}
